import { Box, Typography, Stack } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useMemo, useRef } from 'react';
import { ECElementEvent } from 'echarts';
import { getPieChartWidgetOptions } from '../chartWidgetUtility';
import { useTheme } from '@mui/material/styles';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';

interface DoughnutChartProps {
  isLoading: boolean;
  title?: string;
  tooltipLabel?:
    | string
    | JSX.Element
    | {
        title: string;
        description: string | React.ReactNode;
      }[];
  hasTooltip?: boolean;
  seriesData: { name: string; value: any; percentage: string; formattedName: string }[];
  handleEvent?: (event: ECElementEvent, title?: string) => void;
  dataLabel?: boolean;
  legendLabel?: boolean;
  chartSize?: string;
  children?: React.ReactNode;
  isCost?: boolean;
}

const PieChart = ({
  isLoading = false,
  title = '',
  hasTooltip = false,
  tooltipLabel = '',
  seriesData,
  handleEvent,
  dataLabel = false,
  legendLabel = false,
  chartSize,
  isCost = false,
}: Readonly<DoughnutChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const defaultOptions = useMemo(() => ({
    graphic: [
      {
        type: 'circle',
        left: 'center',
        top: 'center',
        shape: {
          r: 100,
        },
        style: {
          fill: '#e4e4e4',
        },
      },
    ],
    title: {
      show: true,
      textStyle: {
        color: colors.widget.text.main,
        fontWeight: 'normal',
        fontSize: 16,
      },
      text: 'No data',
      left: 'center',
      top: 'center',
    },
    backgroundColor: colors.widget.background.card,
  }), [colors.widget.text.main, colors.widget.background.card]);

  const result = useMemo(
    () => getPieChartWidgetOptions(theme, seriesData, dataLabel, legendLabel, isCost),
    [theme, seriesData, dataLabel, legendLabel, isCost]
  );

  const adjustedOption: any = useMemo(() => {
    if (!seriesData.length) return defaultOptions;

    return {
      ...result,
      series: result.series.map((s: any) => ({
        ...s,
        center: ['50%', '50%'],
      })),
    };
  }, [seriesData.length, result, defaultOptions]);

  const chartEvents = useMemo(() => ({
    click: (params: ECElementEvent) => {
      if (handleEvent) {
        handleEvent(params, title);
      }
    },
  }), [handleEvent, title]);

  return (
    <Box display="flex" flexGrow={1} height="100%" position="relative">
      <Stack
        minHeight="100px"
        p={2}
        bgcolor="primary.main"
        fontFamily="Montserrat"
        borderRadius="5px"
        flexGrow={1}
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
          color: `${colors.widget.text.main} !important`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography
              variant="widgetTitle"
              sx={{ color: `${colors.widget.text.title} !important` }}
            >
              <HighlightedText text={title} />
            </Typography>
            {hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
          </Stack>
        </Stack>
        <Stack
          flexGrow={1}
          justifyContent="center"
          ref={chartContainerRef}
          minHeight={chartSize || '500px'}
        >
          {!isLoading ? (
            <ReactECharts
              style={{ width: '100%', height: '100%' }}
              option={adjustedOption}
              opts={{ renderer: 'canvas', width: 'auto' }}
              notMerge={false}
              lazyUpdate={true}
              onEvents={chartEvents}
            />
          ) : (
            <LoaderComponent />
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PieChart;
